import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAppByIdAsync } from '../../redux/reducer/apps/appSlice';
import { useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography } from '@mui/material';
import AppCurrentVisits from './app/AppCurrentVisits';
import AppWidgetSummary from './app/ApWidgetSummary';
import CircularProgress from '@mui/material/CircularProgress';
import { getAppStats, getNotificationStats } from '../../redux/reducer/auth/api';

function AppDetails() {

  const theme = useTheme();
  const { appId } = useParams();
  const dispatch = useDispatch();

  const [appStats, setAppStats] = useState(null);
  const [notificationStats, setNotificationStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const app = useSelector((state) => state.apps.selectedApp);
  const loading = useSelector((state) => state.apps.loading);

  useEffect(() => {
    dispatch(fetchAppByIdAsync(appId));
  }, [dispatch, appId]);

  useEffect(() => {
    const fetchStats = async () => {
      if (appId) {
        try {
          setStatsLoading(true);
          const [stats, notifStats] = await Promise.all([
            getAppStats(appId),
            getNotificationStats(appId)
          ]);
          setAppStats(stats);
          setNotificationStats(notifStats);
        } catch (error) {
          console.error('Error fetching stats:', error);
        } finally {
          setStatsLoading(false);
        }
      }
    };
    fetchStats();
  }, [appId]);

  if (!app || statsLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">Loading app details...</Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="xl">
          <Typography
            variant="h4"
            fontWeight="700"
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Welcome to {app.title}
          </Typography>

        <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3} >
            <AppWidgetSummary 
              style={{background:"rgb(209, 233, 252)",borderRadius:"12px"}} 
              title="Total Categories" 
              total={appStats?.categoriesCount || 0} 
              icon={'mdi:book-open-page-variant'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(208, 242, 255)",borderRadius:"12px"}} 
              title="Total Courses" 
              total={appStats?.coursesCount || 0} 
              color="info" 
              icon={'mdi:book-multiple'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(255, 247, 205)",borderRadius:"12px"}} 
              title="Total Topics" 
              total={appStats?.topicsCount || 0} 
              color="warning" 
              icon={'mdi:file-document-multiple'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(255, 231, 217)",borderRadius:"12px"}} 
              title="Total Questions" 
              total={appStats?.questionsCount || 0} 
              color="error" 
              icon={'mdi:help-circle'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(230, 247, 237)",borderRadius:"12px"}} 
              title="Total Notes" 
              total={appStats?.notesCount || 0} 
              color="success" 
              icon={'mdi:notebook'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(245, 235, 255)",borderRadius:"12px"}} 
              title="Active Users" 
              total={notificationStats?.totalUsers || 0} 
              color="secondary" 
              icon={'mdi:account-multiple'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(255, 241, 230)",borderRadius:"12px"}} 
              title="Active Devices" 
              total={notificationStats?.activeDevicesCount || 0} 
              color="warning" 
              icon={'mdi:cellphone'} 
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary 
              style={{background:"rgb(230, 243, 255)",borderRadius:"12px"}} 
              title="Reachability" 
              total={`${notificationStats?.reachabilityPercentage || 0}%`} 
              color="info" 
              icon={'mdi:chart-line'} 
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <AppCurrentVisits
              style={{boxShadow:"rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",borderRadius:"12px"}}
              title="Content Distribution"
              chartData={[
                { label: 'Categories', value: appStats?.categoriesCount || 0 },
                { label: 'Courses', value: appStats?.coursesCount || 0 },
                { label: 'Topics', value: appStats?.topicsCount || 0 },
                { label: 'Questions', value: appStats?.questionsCount || 0 },
              ]}
              chartColors={[
                theme.palette.primary.main,
                theme.palette.info.main,
                theme.palette.warning.main,
                theme.palette.error.main,
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <AppCurrentVisits
              style={{boxShadow:"rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",borderRadius:"12px"}}
              title="User Engagement"
              chartData={[
                { label: 'Total Users', value: notificationStats?.totalUsers || 0 },
                { label: 'Active Devices', value: notificationStats?.activeDevicesCount || 0 },
              ]}
              chartColors={[
                theme.palette.success.main,
                theme.palette.secondary.main,
              ]}
            />
          </Grid>

         
         
        </Grid>
      </Container>
      )}
    </MainLayout>
  );
}export default AppDetails;
